import React, { useEffect, useState } from 'react';

import { Button, Loader, Modal, Space, Stepper, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { PasswordInputWithStrength } from 'components/PasswordInputWithValidation';
import { useTranslation } from 'react-i18next';
import { AuthService } from 'services/auth-service';
import { NotificationService } from 'services/notification-service';
import {
  awsResetPasswordCodeValidator,
  confirmPasswordValidator,
  emailValidator,
  requiredInputValidator
} from 'shared/utils/form-validators';

interface ResetPasswordModalProps {
  opened: boolean;
  onClose: () => void;
}

export function ForgetPasswordModal({ opened, onClose }: ResetPasswordModalProps) {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const nextStep = () => setActive((current) => current + 1);

  const resetPasswordForm = useForm({
    initialValues: { email: '' },
    validate: {
      email: (value) => requiredInputValidator(value) || emailValidator(value)
    }
  });

  const verificationCodeForm = useForm({
    initialValues: { code: '', password: '', confirmPassword: '' },
    validate: {
      code: (value) => awsResetPasswordCodeValidator(value),
      password: (value) => requiredInputValidator(value),
      confirmPassword: (value, values) =>
        requiredInputValidator(value) || confirmPasswordValidator(values.password, value)
    }
  });

  const requestFogetPasswordMutation = useMutation({
    mutationFn: AuthService.requestResetPasswordCode,
    onSuccess: () => nextStep(),
    onError: (error: AxiosError) => {
      if (error.response?.status === HttpStatusCode.UnprocessableEntity) {
        NotificationService.showResult(
          false,
          t('AUTH.FORGET_PASSWORD.FAILED_REQUEST_RESET_PASSWORD_WITH_EXTERNAL_PROVIDER')
        );
      } else {
        NotificationService.showResult(false, t('AUTH.FORGET_PASSWORD.FAILED_REQUEST_RESET_PASSWORD'));
      }
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: AuthService.resetFogottenPassword,
    onSuccess: () => {
      NotificationService.showResult(true, t('AUTH.FORGET_PASSWORD.SUCCESS_RESET_PASSWORD'));
      onClose();
    },
    onError: () => NotificationService.showResult(false, t('AUTH.FORGET_PASSWORD.FAILED_RESET_PASSWORD'))
  });

  const handleSubmit = (credentials: { code: string; password: string }) => {
    const isNotValid = verificationCodeForm.validate().hasErrors;
    if (isNotValid) return;
    resetPasswordMutation.mutate({
      email: resetPasswordForm.values.email,
      code: credentials.code,
      password: credentials.password
    });
  };

  useEffect(() => {
    resetPasswordForm.reset();
    verificationCodeForm.reset();
    setActive(0);
  }, [opened]);

  return (
    <>
      <Modal
        opened={opened}
        title={t('AUTH.FORGET_PASSWORD.LABEL')}
        onClose={() => onClose()}
        size="lg"
        styles={{
          inner: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh'
          }
        }}>
        <Stepper active={active} breakpoint="sm">
          <Stepper.Step label={t('AUTH.FORGET_PASSWORD.REQUEST_VERIFICATION_CODE_LABEL')}>
            <Text size="sm">{t('AUTH.FORGET_PASSWORD.REQUEST_VERIFICATION_CODE_HINT')}</Text>
            <Space h={15} />
            <form
              onSubmit={resetPasswordForm.onSubmit((formCredentials) =>
                requestFogetPasswordMutation.mutate({ email: formCredentials.email })
              )}>
              <TextInput
                withAsterisk
                label={t('AUTH.EMAIL.LABEL')}
                placeholder={t('AUTH.EMAIL.PLACEHOLDER')}
                {...resetPasswordForm.getInputProps('email')}
              />

              <Button type="submit" fullWidth mt="xl" disabled={requestFogetPasswordMutation.isLoading}>
                {requestFogetPasswordMutation.isLoading ? (
                  <Loader size="sm" />
                ) : (
                  t('AUTH.FORGET_PASSWORD.REQUEST_VERIFICATION_CODE_BUTTON_LABEL')
                )}
              </Button>
            </form>
          </Stepper.Step>
          <Stepper.Step label={t('AUTH.FORGET_PASSWORD.NEW_CREDENTIAL_LABEL')}>
            <Text size="sm">{t('AUTH.FORGET_PASSWORD.NEW_CREDENTIAL_HINT')}</Text>
            <Space h={15} />
            <form
              onSubmit={verificationCodeForm.onSubmit((data) =>
                handleSubmit({ code: data.code, password: data.password })
              )}>
              <TextInput
                withAsterisk
                label={t('AUTH.FORGET_PASSWORD.VERIFICATION_CODE_INPUT_LABEL')}
                placeholder="123456"
                {...verificationCodeForm.getInputProps('code')}
              />
              <Space h={15} />
              <PasswordInputWithStrength
                passwordInputProps={verificationCodeForm.getInputProps('password')}
                confirmPasswordInputProps={verificationCodeForm.getInputProps('confirmPassword')}
              />
              <Button type="submit" fullWidth mt="xl" disabled={resetPasswordMutation.isLoading}>
                {resetPasswordMutation.isLoading ? (
                  <Loader size="sm" />
                ) : (
                  t('AUTH.FORGET_PASSWORD.RESET_PASSWORD_BUTTON_LABEL')
                )}
              </Button>
            </form>
          </Stepper.Step>
        </Stepper>
      </Modal>
    </>
  );
}
